import { useTick } from "@pixi/react"
import { useCallback, useRef, useState } from "react"

interface PulseAnimationOptions {
  minAlpha?: number
  maxAlpha?: number
  speed?: number
  enabled?: boolean
}

/**
 * A hook that provides a pulsing animation effect, typically used for highlighting elements.
 *
 * @param options - Configuration options for the animation
 * @returns An object containing the current alpha value and a function to stop the animation
 */
export function usePulseAnimation({
  minAlpha = 0.4,
  maxAlpha = 1,
  speed = 0.02,
  enabled = true
}: PulseAnimationOptions = {}) {
  const [alpha, setAlpha] = useState(maxAlpha)
  const direction = useRef(1)
  const speedRef = useRef(speed)
  const isEnabled = useRef(enabled)

  // Allow changing the animation speed
  const setSpeed = useCallback((newSpeed: number) => {
    speedRef.current = newSpeed
  }, [])

  // Enable/disable the animation
  const setEnabled = useCallback((enabled: boolean) => {
    isEnabled.current = enabled
  }, [])

  useTick((delta) => {
    if (!isEnabled.current) return

    // Calculate new alpha based on direction
    const newAlpha = alpha + direction.current * speedRef.current * delta

    // Reverse direction when reaching bounds
    if (newAlpha >= maxAlpha) {
      setAlpha(maxAlpha)
      direction.current = -1
    } else if (newAlpha <= minAlpha) {
      setAlpha(minAlpha)
      direction.current = 1
    } else {
      setAlpha(newAlpha)
    }
  })

  return {
    alpha,
    setEnabled,
    setSpeed
  }
}
